import React, { useState, useEffect } from 'react';
import { useBusinessPlan } from '../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ExportButton from './ui/ExportButton';
import TooltipLabel from './ui/TooltipLabel';
import { Badge } from './ui/badge';
import { 
  FileSpreadsheet, 
  RefreshCw, 
  TrendingUp, 
  Building2, 
  DollarSign,
  Download,
  BarChart3,
  PieChart,
  Target
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  PieChart as RechartsPieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { usePlanningYears } from '../hooks/usePlanningYears';
import { outputTooltips } from '../utils/outputSectionsTooltips';
import { calculateProjectedUnits, formatCurrency as formatCurrencyUtil, formatPercentage as formatPercentageUtil } from '../utils/sharedCalculations';
import { useCurrency } from '../contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialStatements = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(2025);
  const [financialData, setFinancialData] = useState(null);
  const [multiYearData, setMultiYearData] = useState([]);
  
  // Cost Analysis states
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [roiAnalysis, setRoiAnalysis] = useState(null);
  const [costAnalysisLoading, setCostAnalysisLoading] = useState(false);
  
  // Use dynamic planning years
  const planningData = usePlanningYears(currentPlan?.id);
  const { years, loading: yearsLoading } = planningData;
  
  // Update selected year when planning years change
  useEffect(() => {
    if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // Format currency with automatic conversion
  const formatCurrency = (amount) => {
    if (!amount) return formatCurrencyContext(0, currentPlan?.currency || 'USD');
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, false);
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value) return '0%';
    return formatPercentageUtil(value, 1);
  };

  // Fetch financial statements data
  const fetchFinancialData = async () => {
    if (!currentPlan?.id) {
      setError('No business plan selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching financial statements for plan:', currentPlan.id);

      // Fetch current year data
      const currentYearResponse = await axios.get(
        `${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${selectedYear}`
      );
      
      setFinancialData(currentYearResponse.data);
      console.log(`✅ Financial data loaded for ${selectedYear}`);

      // Fetch multi-year data for trends
      try {
        const multiYearPromises = years.map(year =>
          axios.get(`${BACKEND_URL}/api/v3/calculations/financial-statements/${currentPlan.id}?year=${year}`)
        );
        
        const multiYearResponses = await Promise.all(multiYearPromises);
        const yearlyData = multiYearResponses.map((response, index) => ({
          year: years[index],
          data: response.data.financial_statements
        }));
        
        setMultiYearData(yearlyData);
        console.log('✅ Multi-year trend data loaded');
      } catch (err) {
        console.warn('⚠️ Multi-year data fetch failed, continuing with single year');
      }
      
    } catch (err) {
      console.error('Error fetching financial data:', err);
      setError(err.response?.data?.detail || 'Failed to load financial statements');
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or year/plan changes
  useEffect(() => {
    fetchFinancialData();
    if (currentPlan) {
      fetchIntegratedFinancialStatements();
    }
  }, [currentPlan?.id, selectedYear]);

  const fetchIntegratedFinancialStatements = async () => {
    try {
      // Get current date range (last 30 days for monthly)
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(
        `${BACKEND_URL}/api/financial-integration/statements/${currentPlan.id}`,
        {
          params: {
            period_start: startDate,
            period_end: endDate
          }
        }
      );
      
      // Update financial data with integrated values
      setFinancialData(prev => ({
        ...prev,
        integrated_statements: response.data
      }));
      
    } catch (error) {
      console.error('Error fetching integrated financial statements:', error);
    }
  };

  // Fetch Cost Analysis data
  const fetchCostAnalysis = async () => {
    if (!currentPlan?.id) return;
    
    setCostAnalysisLoading(true);
    try {
      const [costResponse, roiResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/v3/calculations/cost-summary/${currentPlan.id}`),
        axios.get(`${BACKEND_URL}/api/v3/calculations/roi-analysis/${currentPlan.id}`)
      ]);
      
      setCostAnalysis(costResponse.data);
      setRoiAnalysis(roiResponse.data);
      console.log('✅ Cost analysis data loaded');
    } catch (error) {
      console.error("Error fetching cost analysis:", error);
      setCostAnalysis(null);
      setRoiAnalysis(null);
    } finally {
      setCostAnalysisLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading financial statements...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <FileSpreadsheet className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Error Loading Financial Statements</h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={fetchFinancialData} className="bg-blue-600 hover:bg-blue-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No plan selected
  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <FileSpreadsheet className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Business Plan Selected</h3>
            <p className="text-slate-600 mb-6">
              Please select or create a business plan to view financial statements.
            </p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-blue-600 hover:bg-blue-700">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const incomeStatement = financialData?.financial_statements?.income_statement;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <TooltipLabel
            label={<h1 className="text-3xl font-bold text-slate-900">Financial Statements</h1>}
            tooltip={outputTooltips.financial_statements.income_statement}
            asChild
          />
          <p className="text-slate-600 mt-1">
            Comprehensive financial reports for {currentPlan.company_name || currentPlan.name}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchFinancialData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <ExportButton
            targetId="financial-statements-content"
            componentName={`Financial_Statements_${selectedYear}`}
            formats={['png', 'xlsx']}
            size="sm"
            variant="outline"
            showIcon={true}
            showText={true}
          />
        </div>
      </div>

      {/* Financial Statements Tabs */}
      <div id="financial-statements-content">
        <Tabs defaultValue="income" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="income">
            <TrendingUp className="h-4 w-4 mr-2" />
            Income Statement
          </TabsTrigger>
          <TabsTrigger value="balance">
            <Building2 className="h-4 w-4 mr-2" />
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="cashflow">
            <DollarSign className="h-4 w-4 mr-2" />
            Cash Flow
          </TabsTrigger>
          <TabsTrigger value="cost-analysis" onClick={fetchCostAnalysis}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Cost Analysis
          </TabsTrigger>
        </TabsList>

        {/* Income Statement Tab */}
        <TabsContent value="income">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main P&L Statement */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Profit & Loss Statement ({selectedYear})</CardTitle>
                </CardHeader>
                <CardContent>
                  {incomeStatement ? (
                    <div className="space-y-4">
                      {/* Revenue Section */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-3 border-b border-slate-200 bg-green-50 px-4 rounded">
                            <span className="font-bold text-green-900">Revenue</span>
                            <span className="font-bold text-green-700 text-lg">
                              {formatCurrency(incomeStatement.revenue)}
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.revenue}
                      />

                      {/* COGS */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-2 pl-4">
                            <span className="text-slate-700">Less: Cost of Goods Sold</span>
                            <span className="text-red-600">
                              ({formatCurrency(incomeStatement.cogs)})
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.cogs}
                      />

                      {/* Gross Profit */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-3 border-b border-slate-200 bg-blue-50 px-4 rounded">
                            <span className="font-semibold text-blue-900">Gross Profit</span>
                            <span className="font-bold text-blue-700">
                              {formatCurrency(incomeStatement.gross_profit)}
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.gross_profit}
                      />

                      {/* Operating Expenses */}
                      <div className="flex justify-between items-center py-2 pl-4">
                        <span className="text-slate-700">Less: Operating Expenses</span>
                        <span className="text-red-600">
                          ({formatCurrency(incomeStatement.operating_expenses)})
                        </span>
                      </div>

                      {/* EBITDA */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-3 border-b border-slate-200 bg-purple-50 px-4 rounded">
                            <span className="font-semibold text-purple-900">EBITDA</span>
                            <span className="font-bold text-purple-700">
                              {formatCurrency(incomeStatement.ebitda)}
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.ebitda}
                      />

                      {/* Depreciation */}
                      <div className="flex justify-between items-center py-2 pl-4">
                        <span className="text-slate-700">Less: Depreciation & Amortization</span>
                        <span className="text-red-600">
                          ({formatCurrency(incomeStatement.depreciation)})
                        </span>
                      </div>

                      {/* EBIT */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-3 border-b border-slate-200 bg-orange-50 px-4 rounded">
                            <span className="font-semibold text-orange-900">EBIT</span>
                            <span className="font-bold text-orange-700">
                              {formatCurrency(incomeStatement.ebit)}
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.ebit}
                      />

                      {/* Interest - Integrated from Financial Management */}
                      <div className="flex justify-between items-center py-2 pl-4">
                        <span className="text-slate-700 flex items-center">
                          Less: Interest Expense
                          {financialData?.integrated_statements && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                              Live Data
                            </span>
                          )}
                        </span>
                        <span className="text-red-600">
                          ({formatCurrency(
                            financialData?.integrated_statements?.interest_expense || 
                            incomeStatement.interest_expense
                          )})
                        </span>
                      </div>

                      {/* PBT */}
                      <div className="flex justify-between items-center py-3 border-b border-slate-200 bg-yellow-50 px-4 rounded">
                        <span className="font-semibold text-yellow-900">Profit Before Tax</span>
                        <span className="font-bold text-yellow-700">
                          {formatCurrency(incomeStatement.pbt)}
                        </span>
                      </div>

                      {/* Tax */}
                      <div className="flex justify-between items-center py-2 pl-4">
                        <span className="text-slate-700">Less: Tax Expense</span>
                        <span className="text-red-600">
                          ({formatCurrency(incomeStatement.tax_expense)})
                        </span>
                      </div>

                      {/* Net Income */}
                      <TooltipLabel
                        label={
                          <div className="flex justify-between items-center py-4 border-t-2 border-slate-400 bg-gradient-to-r from-green-50 to-blue-50 px-4 rounded">
                            <span className="font-bold text-slate-900 text-lg">Net Income</span>
                            <span className={`font-bold text-xl ${incomeStatement.net_income >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(incomeStatement.net_income)}
                            </span>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.net_income_pl}
                      />
                    </div>
                  ) : (
                    <p className="text-slate-600 text-center py-8">No income statement data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Key Ratios Sidebar */}
            <div>
              <Card>
                <CardHeader>
                  <TooltipLabel
                    label={<CardTitle>Key Financial Ratios</CardTitle>}
                    tooltip={outputTooltips.financial_statements.key_ratios}
                    asChild
                  />
                </CardHeader>
                <CardContent>
                  {incomeStatement && financialData?.financial_statements?.key_metrics ? (
                    <div className="space-y-4">
                      <TooltipLabel
                        label={
                          <div className="text-center p-3 bg-green-50 rounded-lg">
                            <p className="text-sm text-green-700">Gross Margin</p>
                            <p className="text-2xl font-bold text-green-900">
                              {formatPercentage(financialData.financial_statements.key_metrics.gross_margin_percent)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.gross_margin_ratio}
                      />
                      <TooltipLabel
                        label={
                          <div className="text-center p-3 bg-purple-50 rounded-lg">
                            <p className="text-sm text-purple-700">EBITDA Margin</p>
                            <p className="text-2xl font-bold text-purple-900">
                              {formatPercentage(financialData.financial_statements.key_metrics.ebitda_margin_percent)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.ebitda_margin_ratio}
                      />
                      <TooltipLabel
                        label={
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-700">Net Margin</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {formatPercentage(financialData.financial_statements.key_metrics.net_margin_percent)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.net_margin_ratio}
                      />
                      <TooltipLabel
                        label={
                          <div className="text-center p-3 bg-orange-50 rounded-lg">
                            <p className="text-sm text-orange-700">Operating Ratio</p>
                            <p className="text-2xl font-bold text-orange-900">
                              {formatPercentage(financialData.financial_statements.key_metrics.operating_ratio)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.operating_ratio}
                      />
                    </div>
                  ) : (
                    <p className="text-slate-600 text-center py-4">No ratio data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Revenue by Stream Breakdown - Phase 5 Integration */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Revenue by Stream
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RevenueByStreamBreakdown selectedYear={selectedYear} currentPlan={currentPlan} />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance">
          <Card>
            <CardHeader>
              <TooltipLabel
                label={<CardTitle>Balance Sheet ({selectedYear})</CardTitle>}
                tooltip={outputTooltips.financial_statements.balance_sheet}
                asChild
              />
            </CardHeader>
            <CardContent>
              {financialData && financialData.financial_statements && financialData.financial_statements.balance_sheet ? (
                <div className="space-y-6">
                  {/* Assets Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-4">Assets</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TooltipLabel
                        label={
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <p className="text-sm text-blue-700">Cash & Cash Equivalents</p>
                            <p className="text-xl font-bold text-blue-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.cash)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.cash}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-green-50 p-4 rounded-lg">
                            <p className="text-sm text-green-700">Accounts Receivable</p>
                            <p className="text-xl font-bold text-green-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.accounts_receivable)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.accounts_receivable}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <p className="text-sm text-purple-700">Fixed Assets</p>
                            <p className="text-xl font-bold text-purple-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.fixed_assets)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.fixed_assets}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-orange-50 p-4 rounded-lg">
                            <p className="text-sm text-orange-700">Total Assets</p>
                            <p className="text-xl font-bold text-orange-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.total_assets)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.total_assets}
                      />
                    </div>
                  </div>

                  {/* Liabilities & Equity Section - Integrated with Financial Management */}
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-4">
                      Liabilities & Equity
                      {financialData?.integrated_statements && (
                        <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Financial Management Integration
                        </span>
                      )}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TooltipLabel
                        label={
                          <div className="bg-red-50 p-4 rounded-lg">
                            <p className="text-sm text-red-700">Accounts Payable</p>
                            <p className="text-xl font-bold text-red-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.accounts_payable)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.accounts_payable}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
                            <p className="text-sm text-yellow-700 flex items-center">
                              Total Liabilities
                              {financialData?.integrated_statements && (
                                <span className="ml-1 text-xs">🔗</span>
                              )}
                              {financialData?.integrated_statements && (
                                <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-1 rounded">
                                  Live: {formatCurrency(financialData.integrated_statements.total_debt)}
                                </span>
                              )}
                            </p>
                            <p className="text-xl font-bold text-yellow-900">
                              {formatCurrency(
                                financialData?.integrated_statements?.total_debt || 
                                financialData.financial_statements.balance_sheet.total_liabilities
                              )}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.total_liabilities}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <p className="text-sm text-indigo-700">Retained Earnings</p>
                            <p className="text-xl font-bold text-indigo-900">
                              {formatCurrency(financialData.financial_statements.balance_sheet.retained_earnings)}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.retained_earnings}
                      />
                      <TooltipLabel
                        label={
                          <div className="bg-emerald-50 p-4 rounded-lg border-2 border-emerald-200">
                            <p className="text-sm text-emerald-700 flex items-center">
                              Total Equity
                              {financialData?.integrated_statements && (
                                <span className="ml-1 text-xs">🔗</span>
                              )}
                              {financialData?.integrated_statements && (
                                <span className="ml-2 text-xs bg-emerald-200 text-emerald-800 px-1 rounded">
                                  Live: {formatCurrency(financialData.integrated_statements.shareholders_equity)}
                                </span>
                              )}
                            </p>
                            <p className="text-xl font-bold text-emerald-900">
                              {formatCurrency(
                                financialData?.integrated_statements?.shareholders_equity || 
                                financialData.financial_statements.balance_sheet.total_equity
                              )}
                            </p>
                          </div>
                        }
                        tooltip={outputTooltips.financial_statements.total_equity}
                      />
                    </div>
                  </div>

                  {/* Balance Sheet Verification */}
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-slate-900 mb-3">Balance Sheet Verification</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-slate-600 mb-1">Total Assets</p>
                        <p className="text-xl font-bold text-blue-700">
                          {formatCurrency(financialData.financial_statements.balance_sheet.total_assets)}
                        </p>
                      </div>
                      <div className="text-center flex items-center justify-center">
                        <span className="text-2xl text-slate-400">=</span>
                      </div>
                      <div className="text-center">
                        <p className="text-slate-600 mb-1">Liabilities + Equity</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(
                            financialData.financial_statements.balance_sheet.total_liabilities + 
                            financialData.financial_statements.balance_sheet.total_equity
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-center">
                      <span className="inline-flex items-center gap-2 text-sm bg-white px-3 py-1 rounded-full">
                        <span className="text-green-600">✓</span>
                        <span className="text-slate-700">Balance Sheet is balanced</span>
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Build Your Balance Sheet</h3>
                    <p className="text-slate-600 mb-4">
                      Your balance sheet will automatically populate once you add financial data
                    </p>
                  </div>

                  {/* What's Needed Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                    <h4 className="font-semibold text-blue-900 mb-4">What affects your Balance Sheet:</h4>
                    
                    <div className="space-y-4">
                      {/* Assets */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">A</span>
                          Assets
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <div>
                              <strong>Cash:</strong> Calculated from net income (add products in Strategic Framework)
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <div>
                              <strong>Accounts Receivable:</strong> Based on revenue (~8% of sales)
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <div>
                              <strong>Fixed Assets:</strong> Add CapEx items in Cost Management
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Liabilities */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-red-100 text-red-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">L</span>
                          Liabilities
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 mt-1">•</span>
                            <div>
                              <strong>Accounts Payable:</strong> Based on revenue (~5% of sales)
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 mt-1">•</span>
                            <div>
                              <strong>Debt:</strong> Add loans in Financial Management → Current Debt
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Equity */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">E</span>
                          Equity
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            <div>
                              <strong>Equity Investment:</strong> Add equity records in Financial Management → Current Equity
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            <div>
                              <strong>Retained Earnings:</strong> Automatically calculated from net income
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3 justify-center flex-wrap">
                      <Button 
                        onClick={() => window.location.href = '/strategic-framework'}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        Add Products
                      </Button>
                      <Button 
                        onClick={() => window.location.href = '/financial-management'}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        Add Loans & Equity
                      </Button>
                      <Button 
                        onClick={() => window.location.href = '/cost-management'}
                        variant="outline"
                        size="sm"
                      >
                        Add CapEx Items
                      </Button>
                    </div>
                  </div>

                  {/* Formula Reference */}
                  <div className="mt-6 text-center text-sm text-slate-500">
                    <p className="font-mono">Assets = Liabilities + Equity</p>
                    <p className="text-xs mt-1">Your balance sheet will always balance automatically</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow">
          <Card>
            <CardHeader>
              <TooltipLabel
                label={<CardTitle>Cash Flow Statement ({selectedYear})</CardTitle>}
                tooltip={outputTooltips.financial_statements.cash_flow}
                asChild
              />
            </CardHeader>
            <CardContent>
              {financialData && financialData.financial_statements && financialData.financial_statements.cash_flow ? (
                <div className="space-y-6">
                  {/* Cash Flow Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <TooltipLabel
                      label={
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-green-700">Operating Cash Flow</p>
                          <p className="text-xl font-bold text-green-900">
                            {formatCurrency(financialData.financial_statements.cash_flow.operating_cash_flow)}
                          </p>
                        </div>
                      }
                      tooltip={outputTooltips.financial_statements.operating_cash_flow}
                    />
                    <TooltipLabel
                      label={
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-blue-700">Investing Cash Flow</p>
                          <p className="text-xl font-bold text-blue-900">
                            {formatCurrency(financialData.financial_statements.cash_flow.investing_cash_flow)}
                          </p>
                        </div>
                      }
                      tooltip={outputTooltips.financial_statements.investing_cash_flow}
                    />
                    <TooltipLabel
                      label={
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-purple-700">Net Cash Flow</p>
                          <p className="text-xl font-bold text-purple-900">
                            {formatCurrency(financialData.financial_statements.cash_flow.net_cash_flow)}
                          </p>
                        </div>
                      }
                      tooltip={outputTooltips.financial_statements.net_cash_flow}
                    />
                  </div>

                  {/* Cash Flow Details */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <TooltipLabel
                      label={<h4 className="font-semibold text-slate-900 mb-4">Cash Flow Breakdown</h4>}
                      tooltip={outputTooltips.financial_statements.cash_flow_breakdown}
                      asChild
                    />
                    
                    {/* Operating Activities Breakdown */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-green-700 mb-2">Operating Activities:</h5>
                      <div className="space-y-2 pl-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">Net Income:</span>
                          <span className="font-medium">{formatCurrency(financialData.financial_statements?.income_statement?.net_income || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <TooltipLabel
                            label={
                              <div className="flex justify-between w-full">
                                <span className="text-slate-700">Add: Depreciation (non-cash)</span>
                                <span className="font-medium text-green-600">+{formatCurrency(financialData.financial_statements?.income_statement?.depreciation || 0)}</span>
                              </div>
                            }
                            tooltip="Depreciation is a non-cash expense, so we add it back to calculate actual cash generated"
                          />
                        </div>
                        <div className="flex justify-between text-sm">
                          <TooltipLabel
                            label={
                              <div className="flex justify-between w-full">
                                <span className="text-slate-700">Less: Working Capital Change</span>
                                <span className="font-medium text-red-600">-{formatCurrency((financialData.financial_statements?.income_statement?.revenue || 0) * 0.05)}</span>
                              </div>
                            }
                            tooltip="Increase in accounts receivable and inventory ties up cash"
                          />
                        </div>
                        <div className="border-t pt-2 flex justify-between font-semibold text-sm">
                          <span className="text-green-900">Operating Cash Flow:</span>
                          <span className="text-green-700">{formatCurrency(financialData.financial_statements.cash_flow.operating_cash_flow)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Investing Activities */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-blue-700 mb-2">Investing Activities:</h5>
                      <div className="space-y-2 pl-4">
                        <div className="flex justify-between text-sm">
                          <TooltipLabel
                            label={
                              <div className="flex justify-between w-full">
                                <span className="text-slate-700">Capital Expenditures (CapEx)</span>
                                <span className="font-medium text-red-600">{formatCurrency(financialData.financial_statements.cash_flow.investing_cash_flow)}</span>
                              </div>
                            }
                            tooltip="Cash spent on purchasing fixed assets like equipment and property"
                          />
                        </div>
                        <div className="border-t pt-2 flex justify-between font-semibold text-sm">
                          <span className="text-blue-900">Investing Cash Flow:</span>
                          <span className="text-blue-700">{formatCurrency(financialData.financial_statements.cash_flow.investing_cash_flow)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Financing Activities */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-purple-700 mb-2">Financing Activities:</h5>
                      <div className="space-y-2 pl-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-700">Debt & Equity Proceeds</span>
                          <span className="font-medium">{formatCurrency(financialData.financial_statements.cash_flow.financing_cash_flow)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between font-semibold text-sm">
                          <span className="text-purple-900">Financing Cash Flow:</span>
                          <span className="text-purple-700">{formatCurrency(financialData.financial_statements.cash_flow.financing_cash_flow)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Net Cash Flow */}
                    <div className="bg-white rounded-lg p-3 border-2 border-slate-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-900">Net Cash Flow:</span>
                        <span className={`text-xl font-bold ${
                          financialData.financial_statements.cash_flow.net_cash_flow >= 0 
                            ? 'text-green-700' 
                            : 'text-red-700'
                        }`}>
                          {formatCurrency(financialData.financial_statements.cash_flow.net_cash_flow)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {financialData.financial_statements.cash_flow.net_cash_flow >= 0 
                          ? '✓ Positive cash generation' 
                          : '⚠ Cash burn - monitor closely'}
                      </p>
                    </div>
                  </div>

                  {/* Link to Advanced Analysis */}
                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-slate-600 mb-3">
                      For multi-year projections and detailed analysis:
                    </p>
                    <Button 
                      onClick={() => window.location.href = '/advanced-calculations'}
                      variant="outline"
                      size="sm"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      View Financial Analytics
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="py-8">
                  <div className="text-center mb-6">
                    <DollarSign className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Track Your Cash Flow</h3>
                    <p className="text-slate-600 mb-4">
                      Cash flow statement shows how money moves in and out of your business
                    </p>
                  </div>

                  {/* What's Needed Section */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
                    <h4 className="font-semibold text-green-900 mb-4">Cash Flow Components:</h4>
                    
                    <div className="space-y-4">
                      {/* Operating Activities */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                          Operating Cash Flow
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            <div>
                              <strong>Net Income:</strong> Add products & costs to generate profit
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            <div>
                              <strong>Depreciation Add-back:</strong> Non-cash expense added back to cash flow
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-green-600 mt-1">•</span>
                            <div>
                              <strong>Working Capital Change:</strong> Changes in accounts receivable/payable
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Investing Activities */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                          Investing Cash Flow
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <div>
                              <strong>CapEx Purchases:</strong> Add capital expenditure items in Cost Management
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-blue-600 mt-1">•</span>
                            <div>
                              <strong>Asset Acquisitions:</strong> Equipment, property, and other fixed assets
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Financing Activities */}
                      <div className="bg-white rounded-lg p-4">
                        <h5 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="bg-purple-100 text-purple-700 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">3</span>
                          Financing Cash Flow
                        </h5>
                        <div className="space-y-2 text-sm ml-8">
                          <div className="flex items-start gap-2">
                            <span className="text-purple-600 mt-1">•</span>
                            <div>
                              <strong>Debt Proceeds:</strong> Add loans in Financial Management
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-purple-600 mt-1">•</span>
                            <div>
                              <strong>Equity Raises:</strong> Add equity investments in Financial Management
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="text-purple-600 mt-1">•</span>
                            <div>
                              <strong>Debt Repayments:</strong> Loan payments reduce financing cash flow
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-6 flex gap-3 justify-center flex-wrap">
                      <Button 
                        onClick={() => window.location.href = '/strategic-framework'}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        Add Products & Revenue
                      </Button>
                      <Button 
                        onClick={() => window.location.href = '/cost-management'}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        Add Costs & CapEx
                      </Button>
                      <Button 
                        onClick={() => window.location.href = '/financial-management'}
                        className="bg-purple-600 hover:bg-purple-700"
                        size="sm"
                      >
                        Add Loans & Equity
                      </Button>
                    </div>
                  </div>

                  {/* Formula Reference */}
                  <div className="mt-6 text-center text-sm text-slate-500">
                    <p className="font-mono">Net Cash Flow = Operating CF + Investing CF + Financing CF</p>
                    <p className="text-xs mt-1">Track how cash moves through your business over time</p>
                  </div>

                  {/* Link to Financial Analytics */}
                  <div className="text-center mt-6">
                    <Button 
                      onClick={() => window.location.href = '/advanced-calculations'}
                      variant="outline"
                      size="sm"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      View Financial Analytics
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cost Analysis Tab */}
        <TabsContent value="cost-analysis">
          {costAnalysisLoading && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <div>
                    <p className="text-blue-800">Loading comprehensive cost analysis...</p>
                    <p className="text-blue-700 text-sm">Analyzing cost structure, strategic allocation, and ROI metrics</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!costAnalysisLoading && (
            <CostAnalysisSection 
              financialData={financialData}
              costAnalysis={costAnalysis}
              roiAnalysis={roiAnalysis}
              selectedYear={selectedYear}
              currentPlan={currentPlan}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              multiYearData={multiYearData}
              fetchCostAnalysis={fetchCostAnalysis}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Multi-Year Trend Preview */}
      {multiYearData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue & Profitability Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {multiYearData.map((yearData) => (
                <div key={yearData.year} className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">{yearData.year}</p>
                  <p className="text-lg font-bold text-slate-900">
                    {formatCurrency(yearData.data?.income_statement?.revenue)}
                  </p>
                  <p className="text-sm text-slate-600">Revenue</p>
                  <p className="text-md font-semibold text-green-700 mt-2">
                    {formatCurrency(yearData.data?.income_statement?.net_income)}
                  </p>
                  <p className="text-xs text-slate-500">Net Income</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

// Revenue by Stream Breakdown Component - Phase 5 Integration
const RevenueByStreamBreakdown = ({ selectedYear, currentPlan }) => {
  const [revenueStreams, setRevenueStreams] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRevenueData = async () => {
      if (!currentPlan?.id) return;
      
      setLoading(true);
      try {
        const [streamsResponse, productsResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/v2/revenue-streams/${currentPlan.id}`),
          axios.get(`${BACKEND_URL}/api/v2/products-enhanced/${currentPlan.id}?include_strategic_only=true`)
        ]);
        
        setRevenueStreams(streamsResponse.data || []);
        setProducts(productsResponse.data || []);
      } catch (error) {
        console.error("Error fetching revenue stream data:", error);
        setRevenueStreams([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRevenueData();
  }, [currentPlan?.id]);

  if (loading) {
    return <div className="text-center py-4 text-slate-600">Loading revenue streams...</div>;
  }

  if (revenueStreams.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-slate-600 text-sm mb-2">No revenue streams created yet</p>
        <button
          onClick={() => window.location.href = '/strategic-framework'}
          className="text-blue-600 hover:text-blue-800 text-xs underline"
        >
          Create Revenue Streams
        </button>
      </div>
    );
  }

  // Helper function to calculate projected units with per-year or single growth rate
  const calculateProjectedUnitsLocal = (product, targetYear) => {
    return calculateProjectedUnits(product, targetYear, 2025);
  };

  // Calculate revenue by stream for the selected year
  const streamRevenue = revenueStreams.map(stream => {
    const linkedProducts = products.filter(p => p.linked_revenue_stream_id === stream.id || p.revenue_stream_id === stream.id);
    const totalRevenue = linkedProducts.reduce((sum, product) => {
      if (product.base_year_units && product.base_price) {
        const projectedUnits = calculateProjectedUnitsLocal(product, selectedYear);
        return sum + (projectedUnits * product.base_price);
      }
      return sum;
    }, 0);

    return {
      ...stream,
      revenue: totalRevenue,
      productCount: linkedProducts.length
    };
  }).sort((a, b) => b.revenue - a.revenue);

  const streamCalculatedRevenue = streamRevenue.reduce((sum, stream) => sum + stream.revenue, 0);
  
  // Note: This revenue may differ from Profit & Loss if products aren't properly linked to streams

  return (
    <div className="space-y-3">
      {streamRevenue.map((stream, index) => {
        const percentage = streamCalculatedRevenue > 0 ? (stream.revenue / streamCalculatedRevenue) * 100 : 0;
        return (
          <div key={stream.id} className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-slate-900 text-sm">
                {stream.stream_name}
              </h4>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                stream.stream_type === 'Product Revenue' ? 'bg-blue-100 text-blue-700' :
                stream.stream_type === 'Service Revenue' ? 'bg-green-100 text-green-700' :
                stream.stream_type === 'Subscription Revenue' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {stream.stream_type}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                {stream.productCount} product{stream.productCount !== 1 ? 's' : ''}
              </span>
              <div className="text-right">
                <div className="font-semibold text-slate-900">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(stream.revenue)}
                </div>
                <div className="text-xs text-slate-500">
                  {percentage.toFixed(1)}% of total
                </div>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-2 w-full bg-slate-200 rounded-full h-1.5">
              <div 
                className="bg-blue-600 h-1.5 rounded-full" 
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>
          </div>
        );
      })}
      
      {streamRevenue.length > 0 && (
        <div className="border-t pt-3 mt-4">
          <div className="flex justify-between items-center font-semibold">
            <span>Total Revenue ({selectedYear})</span>
            <span className="text-green-700">
              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(streamCalculatedRevenue)}
            </span>
          </div>
          <p className="text-xs text-amber-600 mt-1">
            ⚠️ Note: This may differ from Profit & Loss if products aren't linked to revenue streams
          </p>
        </div>
      )}
    </div>
  );
};

// Comprehensive Cost Analysis Section Component
const CostAnalysisSection = ({ 
  financialData, 
  costAnalysis, 
  roiAnalysis, 
  selectedYear, 
  currentPlan,
  formatCurrency,
  formatPercentage,
  multiYearData,
  fetchCostAnalysis
}) => {
  
  // Check if we have data
  const hasCostData = financialData?.financial_statements?.cost_breakdown;
  const hasStrategicData = costAnalysis || roiAnalysis;

  // Empty state
  if (!hasCostData && !hasStrategicData) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            <BarChart3 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No Cost Data Available</h3>
            <p className="text-slate-600 mb-4">
              Start building your cost structure to see comprehensive analysis
            </p>
            <div className="space-y-2 text-sm text-left max-w-md mx-auto mb-6">
              <div className="flex items-center gap-2">
                <span className="text-blue-600">→</span>
                <span>Add cost items in <strong>Cost Management</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-600">→</span>
                <span>Link costs to initiatives in <strong>Strategic Framework</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-600">→</span>
                <span>Set budgets for strategic initiatives</span>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => window.location.href = '/cost-management'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Go to Cost Management
              </Button>
              <Button 
                onClick={() => window.location.href = '/strategic-framework'}
                variant="outline"
              >
                Go to Strategic Framework
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare cost breakdown data for charts
  const costBreakdown = hasCostData ? financialData.financial_statements.cost_breakdown : {};
  const costCategories = Object.entries(costBreakdown).map(([category, amount]) => ({
    category: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
    amount: amount,
    fill: getCategoryColor(category)
  }));

  // Calculate cost metrics
  const totalCosts = Object.values(costBreakdown).reduce((sum, val) => sum + val, 0);
  const fixedCosts = (costBreakdown.fixed || 0) + (costBreakdown.indirect || 0);
  const variableCosts = (costBreakdown.variable || 0) + (costBreakdown.direct || 0);
  const semiVariableCosts = costBreakdown.semi_variable || 0;
  
  // Cost structure percentages
  const fixedPercent = totalCosts > 0 ? (fixedCosts / totalCosts) * 100 : 0;
  const variablePercent = totalCosts > 0 ? (variableCosts / totalCosts) * 100 : 0;
  const semiVariablePercent = totalCosts > 0 ? (semiVariableCosts / totalCosts) * 100 : 0;

  // Multi-year cost trends
  const costTrends = multiYearData.map(yearData => ({
    year: yearData.year,
    totalCosts: Object.values(yearData.data?.cost_breakdown || {}).reduce((sum, val) => sum + val, 0),
    operatingExpenses: yearData.data?.income_statement?.operating_expenses || 0,
    cogs: yearData.data?.income_statement?.cogs || 0
  }));

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Comprehensive Cost Analysis</h2>
          <p className="text-slate-600">Financial cost structure and strategic cost allocation</p>
        </div>
        <Button
          onClick={fetchCostAnalysis}
          variant="outline"
          size="sm"
          className="flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh Data</span>
        </Button>
      </div>

      {/* Cost Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Costs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(totalCosts)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Fixed Costs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(fixedCosts)}
                </p>
                <p className="text-xs text-slate-500">{fixedPercent.toFixed(1)}% of total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Variable Costs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(variableCosts)}
                </p>
                <p className="text-xs text-slate-500">{variablePercent.toFixed(1)}% of total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Average Initiative ROI</p>
                <p className="text-2xl font-bold text-slate-900">
                  {roiAnalysis?.analytics_summary ? 
                    `${roiAnalysis.analytics_summary.avg_initiative_roi.toFixed(1)}%` : '0%'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost Category Breakdown - Bar Chart */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
              Cost Category Breakdown
            </CardTitle>
            <CardDescription>Distribution of costs across categories ({selectedYear})</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costCategories}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="category" 
                    stroke="#64748b"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                  />
                  <YAxis stroke="#64748b" />
                  <RechartsTooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="amount" 
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Fixed vs Variable - Pie Chart */}
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <PieChart className="h-5 w-5 mr-2 text-green-600" />
              Cost Structure Analysis
            </CardTitle>
            <CardDescription>Fixed, Variable, and Semi-Variable cost distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={[
                      { name: 'Fixed Costs', value: fixedCosts, fill: '#3b82f6' },
                      { name: 'Variable Costs', value: variableCosts, fill: '#10b981' },
                      { name: 'Semi-Variable', value: semiVariableCosts, fill: '#f59e0b' }
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                    outerRadius={80}
                    dataKey="value"
                  >
                    <Cell fill="#3b82f6" />
                    <Cell fill="#10b981" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Multi-Year Cost Trends */}
      {costTrends.length > 0 && (
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-purple-600" />
              Cost Trends Over Time
            </CardTitle>
            <CardDescription>Multi-year cost projections and trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={costTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="year" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <RechartsTooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="totalCosts" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    name="Total Costs"
                    dot={{ fill: '#8b5cf6', r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="operatingExpenses" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    name="Operating Expenses"
                    dot={{ fill: '#3b82f6', r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cogs" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="COGS"
                    dot={{ fill: '#10b981', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strategic Cost Allocation (from Strategic Framework) */}
      {hasStrategicData && roiAnalysis && roiAnalysis.initiative_level_roi && roiAnalysis.initiative_level_roi.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Initiative ROI Analysis */}
          <Card className="glass-card shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                Initiative ROI Analysis
              </CardTitle>
              <CardDescription>Return on investment by strategic initiative</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roiAnalysis.initiative_level_roi.slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="initiative_name" 
                      stroke="#64748b"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      fontSize={12}
                    />
                    <YAxis stroke="#64748b" />
                    <RechartsTooltip 
                      formatter={(value) => [`${value.toFixed(1)}%`, 'ROI']}
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="roi_percentage" 
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Strategic Cost Allocation by Objective */}
          {costAnalysis && costAnalysis.objectiveCosts && (
            <Card className="glass-card shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <PieChart className="h-5 w-5 mr-2 text-blue-600" />
                  Cost Allocation by Objective
                </CardTitle>
                <CardDescription>Strategic cost distribution across objectives</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={Object.entries(costAnalysis.objectiveCosts)
                          .map(([objId, cost], index) => ({
                            name: `Objective ${index + 1}`,
                            value: cost,
                            fill: `hsl(${index * 45}, 70%, 60%)`
                          }))
                          .filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(costAnalysis.objectiveCosts).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                        ))}
                      </Pie>
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value), 'Cost']}
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Cost Efficiency Metrics */}
      {financialData?.financial_statements && (
        <Card className="glass-card shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Target className="h-5 w-5 mr-2 text-orange-600" />
              Cost Efficiency Metrics
            </CardTitle>
            <CardDescription>Key cost performance indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Operating Ratio</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatPercentage(financialData.financial_statements.key_metrics?.operating_ratio || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Operating Expenses / Revenue</p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">COGS Ratio</p>
                <p className="text-2xl font-bold text-slate-900">
                  {financialData.financial_statements.income_statement?.revenue > 0 
                    ? formatPercentage((financialData.financial_statements.income_statement.cogs / 
                       financialData.financial_statements.income_statement.revenue) * 100)
                    : '0%'}
                </p>
                <p className="text-xs text-slate-500 mt-1">COGS / Revenue</p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Strategic Costs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(costAnalysis?.totalStrategicCosts || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Linked to initiatives</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Analysis Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3">Cost Analysis Insights</h3>
          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>
                Your cost structure is <strong>{fixedPercent > 50 ? 'fixed-heavy' : 'variable-heavy'}</strong> with 
                {' '}<strong>{fixedPercent.toFixed(1)}%</strong> fixed costs and 
                {' '}<strong>{variablePercent.toFixed(1)}%</strong> variable costs
              </span>
            </div>
            {roiAnalysis?.analytics_summary && (
              <div className="flex items-start gap-2">
                <span className="text-green-600 mt-1">•</span>
                <span>
                  Average initiative ROI is <strong>{roiAnalysis.analytics_summary.avg_initiative_roi.toFixed(1)}%</strong>
                  {roiAnalysis.analytics_summary.avg_initiative_roi > 15 ? ' (Excellent performance)' : roiAnalysis.analytics_summary.avg_initiative_roi > 0 ? ' (Positive return)' : ' (Needs improvement)'}
                </span>
              </div>
            )}
            {costTrends.length > 1 && (
              <div className="flex items-start gap-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>
                  Cost trend shows{' '}
                  {((costTrends[costTrends.length - 1].totalCosts - costTrends[0].totalCosts) / costTrends[0].totalCosts * 100).toFixed(1)}%
                  {' '}growth over {costTrends.length} years
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper function to get category colors
const getCategoryColor = (category) => {
  const colors = {
    fixed: '#3b82f6',
    variable: '#10b981',
    semi_variable: '#f59e0b',
    direct: '#8b5cf6',
    indirect: '#ec4899',
    opex: '#06b6d4',
    capex: '#f97316',
    financial: '#ef4444',
    taxes: '#84cc16'
  };
  return colors[category] || '#64748b';
};

export default FinancialStatements;